import { fetchFlights } from 'actions/FlightActions'
import CurrencyContext from 'contexts/currencyContext'
import { getFlightSearchKey } from 'lib/flightUtils'
import { useContext, useEffect, useMemo } from 'react'
import { EmptyObject } from 'lib/objectUtils'
import { arrayToMap, groupBy, min, nonNullable } from 'lib/arrayUtils'
import { isJourneyV1, isJourneyV2 } from 'lib/flights'
import { useAppDispatch, useAppSelector } from './reduxHooks'
import { FlightViewTypes } from 'constants/flight'

export interface FlightSearchParams {
  startDate: string;
  endDate: string;
  origin: string;
  destination: string;
  occupancies: Array<App.Occupants>;
  fareClass: string;
  maxArrivalTime?: string;
  minReturningDepartureTime?: string;
  carriers?: Array<string>;
  carrier?: string;
  fareType?: string;
  viewType?: string;
  flightsCredit?: boolean;
  forceBundleId?: string;
  source?: string;
}

function useSearchFlights(
  params: FlightSearchParams,
  options: {
    disabled?: boolean,
    departingFlight?: App.AnyJourney,
    segment?: 'departing' | 'returning',
    departingFareFamily?: App.FlightFareFamily,
  } = EmptyObject,
): [Array<App.AnyJourney>, boolean, any, string | undefined, string | undefined, string] {
  const currency = useContext(CurrencyContext)
  const { disabled, departingFlight, segment, departingFareFamily } = options

  const paramsWithCurrency = {
    ...params,
    currency,
  }
  const key = getFlightSearchKey(paramsWithCurrency)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!disabled) {
      dispatch(fetchFlights(paramsWithCurrency))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, disabled])

  const flights = useAppSelector(state => state.flights.searchFlights[key])
  const flightsV2 = useAppSelector(state => state.flights.searchV2Flights[key])

  const v1Journeys = useMemo((): Array<App.Journey> => {
    if (flights && segment) {
      if (segment === 'departing') {
        // Make sure we only show the cheapest version of the same flight
        const flightGroups = groupBy(flights.journeys, flight => flight.departing.journeyKey)
        return Array.from(flightGroups.values()).map(group => min(group, group => group.cost)!)
      } else {
        if (isJourneyV1(departingFlight)) {
          // we have a departing flight that expects a return leg, only return flights that pair with this one
          return flights.journeys.filter(flight => flight.departing.journeyKey === departingFlight.departing.journeyKey)
        }
        return flights.journeys
      }
    }
    return flights?.journeys ?? []
  }, [flights, departingFlight, segment])

  const v2Journeys = useMemo((): Array<App.JourneyV2> => {
    if (flightsV2 && segment) {
      if (segment === 'departing') {
        // departing flights are set at first array of return results
        return flightsV2.departingFares
      } else {
        const returnFares = flightsV2.returningFares
        if (isJourneyV2(departingFlight) && departingFlight.fareType === FlightViewTypes.RETURN) {
          let currentDepartingFlight = departingFlight

          // if a departing fare family is selected, we need to find the corresponding departing flight to get the correct returning flights
          if (departingFareFamily) {
            currentDepartingFlight = flightsV2.departingFares.filter((fare) => {
              return fare.carrier === departingFlight.carrier && fare.fareType === departingFlight.fareType &&
                fare.flightGroup.flights.length === departingFlight.flightGroup.flights.length &&
                fare.flightGroup.flights.every((flight, index) => {
                  return flight.departingTime === departingFlight.flightGroup.flights[index].departingTime &&
                    flight.arrivalTime === departingFlight.flightGroup.flights[index].arrivalTime
                }) && fare.bookingInfo.bookingClass === departingFareFamily.bookingClass
            })[0] || departingFlight
          }

          // We have a departing flight that expects a return leg, find all return flights that pair with this one
          const returnFairsById = arrayToMap(returnFares, fare => fare.bookingInfo.boookingKey)
          return nonNullable(currentDepartingFlight.pairedFareId.map(id => returnFairsById.get(id)))
        } else {
          // Doesn't have a paired departing flight, so we must be looking for one ways only
          return returnFares.filter(fare => fare.fareType !== FlightViewTypes.RETURN)
        }
      }
    }
    return flightsV2?.departingFares.concat(flightsV2.returningFares) ?? []
  }, [departingFlight, flightsV2, segment, departingFareFamily])

  const fetching = useMemo(() => {
    if (!flights && !flightsV2) {
      return true
    }

    return !!(flightsV2 ? flightsV2.fetching : flights?.fetching)
  }, [flights, flightsV2])

  return [
    v1Journeys.length === 0 ? v2Journeys : v1Journeys,
    fetching,
    flightsV2 ? flightsV2.error : flights?.error,
    flightsV2?.searchId,
    flightsV2?.viewType,
    key,
  ]
}

export default useSearchFlights
